import clsx from 'clsx';
import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatNumber } from 'translate';
import RcTooltip from 'rc-tooltip';
import styled from 'styled-components';
import { getCurrencyFormatterData } from '../../_core/utils';
import CurrencyFormatterContent from './CurrencyFormatterContent';

const StyledCurrencyFormatterWrapper = styled.span`
  display: inline-block;
  & > * {
    display: flex;
    align-items: center;
  }
`;

// List from src/styles/_currency.scss
const availableCurrencies = [
  'eth', '2key', 'btc', 'viacoin',
  'dop', 'crc', 'brl', 'zar', 'pln', 'jpy', 'huf', 'sek', 'dkk', 'nok', 'czk', 'myr', 'ngn',
  'inr', 'pen', 'php', 'qar', 'ron', 'mdl', 'uah', 'uyu', 'uzs', 'bgn', 'kzt', 'vnd',
  'eur', 'gbp', 'usd', 'cad', 'aud', 'hkd', 'ars', 'cop', 'clp', 'mxn', 'sgd', 'nad',
  'yen', 'rub', 'krw', 'try', 'ils', 'nis', 'rupee', 'dai', 'usdt', 'tusd', 'pax', 'usdc', 'wbtc', 'renbtc', 'busd',
];

const CurrencyFormatter = ({
  className,
  value = 0,
  defaultValue,
  currency: currencyProp,
  style,
  tagName,
  // #2392 - see PageSummary for sample usage
  customCurrencyIcons: Icon,
  isCustomFormatter,
  skipZero,
  isColored,
  withSign = '',
  prefixSign,
  denseIcon,
  showSuffix,
  maximumFractionDigits,
}) => {
  const [formattedValue, prefix, tooltipValue] = useMemo(() => {
    const funcProps =
      [value, prefixSign, defaultValue, maximumFractionDigits, formatNumber, skipZero, style, isCustomFormatter];
    return getCurrencyFormatterData(...funcProps);
  }, [value, prefixSign, defaultValue, maximumFractionDigits, skipZero, style, isCustomFormatter]);

  const [currency, currencyLowered] = useMemo(() => {
    const currencyLocal = currencyProp && currencyProp.replace('_', ''); // for '_2KEY' case
    const currencyLoweredLocal = currencyLocal && currencyLocal.toLowerCase();

    return [currencyLocal, currencyLoweredLocal];
  }, [currencyProp]);

  const icon = useMemo(() => {
    let iconLocal = null;

    if (currencyLowered) {
      if (Icon) {
        iconLocal = Icon[currencyLowered];
      }

      if (!iconLocal && availableCurrencies.includes(currencyLowered)) {
        iconLocal = (<i className={clsx(
          `currency-symbol-${currencyLowered}${isColored ? '-colored' : ''}`,
          { 'dense-icon': denseIcon }
        )}
        />);
      }
    }

    return iconLocal;
  }, [currencyLowered, denseIcon, isColored]);

  const containerProps = {};

  if (tagName !== Fragment) {
    containerProps.className = className;
  }

  if (tooltipValue) {
    return (
      <RcTooltip
        overlayClassName="twokey-tooltip-wrap tooltip-auto-width"
        placement="top"
        overlay={tooltipValue}
      >
        <StyledCurrencyFormatterWrapper>
          <CurrencyFormatterContent
            containerProps={containerProps}
            prefix={prefix}
            tagName={tagName}
            icon={icon}
            withSign={withSign}
            formattedValue={formattedValue}
            showSuffix={showSuffix}
            currency={currency}
          />
        </StyledCurrencyFormatterWrapper>
      </RcTooltip>
    );
  }

  return (
    <CurrencyFormatterContent
      containerProps={containerProps}
      prefix={prefix}
      tagName={tagName}
      icon={icon}
      withSign={withSign}
      formattedValue={formattedValue}
      showSuffix={showSuffix}
      currency={currency}
    />
  );
};

CurrencyFormatter.propTypes = {
  className: PropTypes.string,
  prefixSign: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  value: PropTypes.number,
  skipZero: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currency: PropTypes.string,
  style: PropTypes.oneOf(['short', 'long']),
  tagName: PropTypes.elementType,
  customCurrencyIcons: PropTypes.object,
  isCustomFormatter: PropTypes.bool,
  isColored: PropTypes.bool,
  showSuffix: PropTypes.bool,
  withSign: PropTypes.string,
  denseIcon: PropTypes.bool,
  maximumFractionDigits: PropTypes.number,
};

CurrencyFormatter.defaultProps = {
  prefixSign: '',
  className: '',
  value: 0,
  style: 'long',
  tagName: 'span',
  isColored: false,
  denseIcon: false,
  showSuffix: false,
  isCustomFormatter: false,
  maximumFractionDigits: 2,
};

export default CurrencyFormatter;
